import React from 'react';
import {useSelector} from 'react-redux';
import PageButton from '../elements/PageButton/PageButton';
import {getLeagueClassName} from '../../utils/rating/getLeagueByRating';
import {RANKED_SEARCH_HINT_STORAGE_KEY, useDriftingSearchHint} from '../../hooks/other/useDriftingSearchHint';
import {useIsDailyDoubleRewardAvailable} from '../../hooks/rewards/useIsDailyDoubleRewardAvailable';
import '../MainMenu/MainMenu.css';

// Бейдж "Награда x2"/x4 на кнопке рейтингового матча имеет смысл только у
// игрока с достаточным выбором фракций для рейтинговых партий — иначе он
// рекламирует бонус тем, кому по факту почти не из чего выбирать
const MIN_UNLOCKED_FACTIONS_FOR_DOUBLE_REWARD_BADGE = 4;

/**
 * Кнопка рейтингового матча: значок лиги игрока, подсказка «Время поиска
 * матча» и анимированный бейдж двойной награды за первый рейтинговый матч дня
 * (с премиумом — x4). Одна на главное меню («Рейтинговый матч» — открывает
 * экран настройки поиска) и на экран настройки («Начать поиск»), чтобы бейдж и
 * подсказка не разошлись между ними.
 */
function RankedMatchButton({label, onClick, pageId, buttonId, isGuest, className = ''}) {
  const auth = useSelector((state) => state.private.auth);
  // Общий ключ хранения с экраном поиска: «среднее время ожидания» там то же
  const rankedSearchSeconds = useDriftingSearchHint(RANKED_SEARCH_HINT_STORAGE_KEY, 25, 57, 1, 5);

  // Доступность x2 награды вычисляется на клиенте из сырой даты последнего
  // получения (auth.lastDoubleRewardAt) с автопересчетом игровых суток
  // (см. getGameDayKey) — бейдж зажигается после полуночи даже на давно
  // открытой странице
  const isDailyDoubleRewardAvailable = useIsDailyDoubleRewardAvailable(auth.lastDoubleRewardAt);
  const unlockedFactionsCount = (auth.availableFactions || []).filter((f) => f.isAvailable).length;
  const hasEnoughFactionsForDoubleRewardBadge = unlockedFactionsCount >= MIN_UNLOCKED_FACTIONS_FOR_DOUBLE_REWARD_BADGE;
  // Логика активности премиума — та же, что и в UserHeader
  const isPremiumActive = Boolean(auth.premiumExpiresAt)
    && new Date(auth.premiumExpiresAt).getTime() > Date.now();

  return (
    <PageButton
      className={`main-menu-button_rating main-menu-button_with-hint ${getLeagueClassName(auth.rating)} ${className}`}
      onClick={onClick} pageId={pageId} buttonId={buttonId} isGuest={isGuest}
    >
      <span className="main-menu-button_label">{label}</span>
      <span className="main-menu-button_hint">
        Время поиска матча: <span className="text-attention">{rankedSearchSeconds} сек</span>
      </span>
      {isDailyDoubleRewardAvailable && hasEnoughFactionsForDoubleRewardBadge && (
        isPremiumActive ? (
          /* Премиум: та же грид-наложенная кросс-фейд карусель, но по
             кругу из трех текстов (Премиум -> Награда х4 -> первый матч
             дня) — своя длительность анимации и раскладка задержек,
             см. main-menu-double-reward-badge-text_triple-* в CSS */
          <span className="main-menu-double-reward-badge">
            <span
              className="main-menu-double-reward-badge-text main-menu-double-reward-badge-text_triple-first"
            >ПРЕМИУМ</span>
            <span
              className="main-menu-double-reward-badge-text main-menu-double-reward-badge-text_triple-second"
            >Награда х4</span>
            <span
              className="main-menu-double-reward-badge-text main-menu-double-reward-badge-text_triple-third"
            >первый матч дня</span>
          </span>
        ) : (
          /* Бейдж «двойная награда доступна»: оба текста отрисованы всегда
             (грид-наложение — ширина бейджа по самому длинному, не прыгает),
             opacity сменяет их в противофазе кроссфейдом */
          <span className="main-menu-double-reward-badge">
            <span className="main-menu-double-reward-badge-text">Награда х2</span>
            <span
              className="main-menu-double-reward-badge-text main-menu-double-reward-badge-text_second"
            >первый матч дня</span>
          </span>
        )
      )}
    </PageButton>
  );
}

export default RankedMatchButton;
